import React, { useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Alert, Box, CircularProgress, Step, StepLabel, Stepper, Typography } from '@mui/material';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { Button } from '../../../components/library';
import { PageWrapper } from '../../../styledComponents/wrappers';
import { PackOrder, PackSession } from '../../../services/fulfillment/types';
import { getOrCreateSession, getPackOrder } from '../../../services/fulfillment/packApi';
import MockPackScanStep from './MockPackScanStep';
import MockPackSlipsStep from './MockPackSlipsStep';
import MockPackLabelsStep from './MockPackLabelsStep';

const STEPS = ['Pack', 'Packing Slips', 'Labels & Ship'];

export const MOCK_PACK_DETAIL_ROUTE = '/fulfillment/mock/pack/:orderId';

const MockPackDetail: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { orderId } = useParams<{ orderId: string }>();

  const [order, setOrder] = useState<PackOrder | null>(null);
  const [session, setSession] = useState<PackSession | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const stepParam = new URLSearchParams(location.search).get('step');
    if (stepParam) setActiveStep(Math.min(Math.max(parseInt(stepParam, 10), 0), 2));
  }, [location.search]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const o = await getPackOrder(Number(orderId));
        if (!o) {
          setError('Order not found.');
          return;
        }
        const s = await getOrCreateSession(o);
        setOrder(o);
        setSession(s);
      } catch {
        setError('Failed to load order.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [orderId]);

  if (loading) {
    return (
      <PageWrapper>
        <Box display="flex" justifyContent="center" mt={8}>
          <CircularProgress />
        </Box>
      </PageWrapper>
    );
  }

  if (error || !order || !session) {
    return (
      <PageWrapper>
        <Alert severity="error">{error ?? 'Order not found.'}</Alert>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Button
        ordinality="secondary"
        variant="text"
        startIcon={<ArrowBackIcon />}
        onClick={() => history.push('/fulfillment/mock/pack')}
      >
        Pack Queue
      </Button>

      <Box sx={{ mt: 2, mb: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="h5">{order.shopifyOrderName}</Typography>
        {order.isPriority && (
          <Box
            sx={{
              px: 1,
              py: 0.25,
              bgcolor: '#fef08a',
              color: '#713f12',
              borderRadius: 1,
              fontSize: 11,
              fontWeight: 700,
              border: '1px solid #fde047',
            }}
          >
            PRIORITY
          </Box>
        )}
      </Box>
      <Typography sx={{ fontSize: 13, color: 'grey.600', mb: 3 }}>
        {order.customer.firstName} {order.customer.lastName} · {order.lines.length} item
        {order.lines.length !== 1 ? 's' : ''}
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {STEPS.map(label => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {activeStep === 0 && (
        <MockPackScanStep
          order={order}
          session={session}
          onSessionChange={setSession}
          onNext={() => setActiveStep(1)}
        />
      )}
      {activeStep === 1 && (
        <MockPackSlipsStep
          order={order}
          session={session}
          onSessionChange={setSession}
          onNext={() => setActiveStep(2)}
          onBack={() => setActiveStep(0)}
        />
      )}
      {activeStep === 2 && (
        <MockPackLabelsStep
          order={order}
          session={session}
          onSessionChange={setSession}
          onDone={() => history.push('/fulfillment/mock/pack')}
          onBack={() => setActiveStep(1)}
        />
      )}
    </PageWrapper>
  );
};

export default MockPackDetail;
